import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match, P } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  FoundUser,
  InvolvementAdditionUnbranded,
  Organisation,
  PersonInvolvement,
  PersonInvolvementKind,
  StartInvolvementsRequest,
} from "@thalamos/haven-common";
import {
  hasInvolvementEnded,
  involvementKindExcludedBy,
  isInvolvementKindHeldByOneAtMost,
  isOrganisationDistantFromPerson,
  organisationTypesEligibleFor,
  partyKey,
  StartInvolvementsRequestSchema,
  USER_FIND_MIN_TERM_LENGTH,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";
import type { PartyKind } from "../CaseInvolvement/CaseInvolvement.js";
import {
  ChangedSinceOpenedAlert,
  outcomeOfChange,
  type InvolvementChangeStatus,
  type InvolvementOutcome,
  type PartySearch,
} from "../InvolvementDialogs/InvolvementDialogs.js";
import {
  DistanceWarning,
  offersFor,
  useSettledResults,
  type Offer,
} from "../PartyPickers/PartyPickers.js";

/**
 * The role being added to, and whether people or bodies take it — only the
 * pairs parties are added to. Who runs the case is named or passed on, and who
 * opened the record is never changed, so neither has a dialog here.
 */
export type AddingTo =
  | {
      partyKind: "user";
      kind: "designated_assessor" | "approver" | "assessor" | "viewer";
    }
  | {
      partyKind: "organisation";
      kind: "duty_of_care" | "placement" | "viewer";
    };

/**
 * The dialog to open for a role's Add on the case involvement tab, or
 * `undefined` for a role this dialog does not add to.
 */
export const addingTo = (
  kind: PersonInvolvementKind,
  partyKind: PartyKind,
): AddingTo | undefined =>
  match({ kind, partyKind })
    .returnType<AddingTo | undefined>()
    .with(
      {
        partyKind: "user",
        kind: P.union("designated_assessor", "approver", "assessor", "viewer"),
      },
      (pair) => pair,
    )
    .with(
      {
        partyKind: "organisation",
        kind: P.union("duty_of_care", "placement", "viewer"),
      },
      (pair) => pair,
    )
    .otherwise(() => undefined);

/** A party waiting on the list to be added when the dialog is confirmed. */
type Listed = {
  party: InvolvementAdditionUnbranded;
  label: string;
  /** For a body, its name — everyone there will reach the record; `null` for a person. */
  organisationName: string | null;
};

/** Why a party cannot go on the list, or `null` where it can. */
type Unavailable = (key: string) => string | null;

export type AddInvolvementsDialogProps = {
  /** The role to add to, or `null` while the dialog is closed. */
  adding: AddingTo | null;
  personName: string;
  involvements: readonly PersonInvolvement[];
  search: PartySearch;
  onAdd: (request: StartInvolvementsRequest) => Promisable<InvolvementOutcome>;
  onRefresh: () => Promisable<void>;
  onClose: () => void;
};

const keyOf = (involvement: PersonInvolvement): string =>
  partyKey(involvement.party);

/**
 * Who holds a role in the sense the server counts: an involvement that has
 * expired still holds it until somebody ends it, and one whose ending is still
 * to come holds it until then.
 */
const unendedHoldersOf = (
  involvements: readonly PersonInvolvement[],
  kind: PersonInvolvementKind | undefined,
): Set<string> =>
  new Set(
    involvements
      .filter(
        (involvement) =>
          involvement.kind === kind && !hasInvolvementEnded(involvement),
      )
      .map(keyOf),
  );

const Searching = () => {
  const { t } = useTranslation();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <CircularProgress size={16} />
      <Typography variant="body2" color="text.secondary">
        {t("access.picker.searching")}
      </Typography>
    </Stack>
  );
};

/**
 * Finds a person, then the body they are involved on behalf of.
 *
 * Only people whose role somewhere lets them take this part are listed at all;
 * somebody who could but may not — already holding it, say — is listed greyed
 * with the reason, so the absence of a familiar name is not a mystery.
 */
const PersonAdder = ({
  kind,
  search,
  unavailable,
  disabled,
  onList,
}: {
  kind: PersonInvolvementKind;
  search: PartySearch;
  unavailable: Unavailable;
  disabled: boolean;
  onList: (listed: Listed) => void;
}) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const [chosen, setChosen] = useState<{
    user: FoundUser;
    offers: Offer[];
  } | null>(null);
  const [offerKey, setOfferKey] = useState("");

  const longEnough = term.trim().length >= USER_FIND_MIN_TERM_LENGTH;
  const { results, loading } = useSettledResults(
    term.trim(),
    longEnough && chosen === null,
    search.findUsers,
  );

  const candidates = results.flatMap((user) => {
    // No guest: see `InvolvementAdditionUnbranded`.
    const usable = offersFor(user, kind, search.near).filter(
      (offer) => offer.eligible,
    );
    // Somebody may hold several roles at one body, and the involvement records
    // only the body, so each body is offered once.
    const offers = usable.filter(
      (offer, index) =>
        usable.findIndex(
          (other) =>
            other.chosen.membership?.organisationId ===
            offer.chosen.membership?.organisationId,
        ) === index,
    );

    return offers.length === 0
      ? []
      : [
          {
            user,
            offers,
            reason: unavailable(partyKey({ kind: "user", userId: user.id })),
          },
        ];
  });

  const offer = chosen?.offers.find((offered) => offered.key === offerKey);

  const choose = (user: FoundUser, offers: Offer[]) => {
    setChosen({ user, offers });
    setTerm(user.name);
    // With one way to involve them there is nothing to choose, and asking
    // would be a question with one answer.
    setOfferKey(offers.length === 1 ? (offers[0]?.key ?? "") : "");
  };

  const reset = () => {
    setChosen(null);
    setTerm("");
    setOfferKey("");
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <TextField
          size="small"
          label={t("access.addInvolvements.searchPeople")}
          helperText={t("access.picker.searchHint", {
            count: USER_FIND_MIN_TERM_LENGTH,
          })}
          disabled={disabled}
          value={term}
          onChange={(event) => {
            setChosen(null);
            setOfferKey("");
            setTerm(event.target.value);
          }}
        />

        {loading ? <Searching /> : null}

        {chosen === null &&
        longEnough &&
        !loading &&
        candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("access.picker.noResults")}
          </Typography>
        ) : null}

        {chosen === null ? (
          <List dense disablePadding>
            {candidates.map(({ user, offers, reason }) => (
              <ListItemButton
                key={user.id}
                disabled={disabled || reason !== null}
                onClick={() => {
                  choose(user, offers);
                }}
              >
                <ListItemText
                  primary={user.name}
                  secondary={
                    <>
                      {offers.length === 1
                        ? offers[0]?.label
                        : t("access.addInvolvements.memberships", {
                            count: offers.length,
                          })}
                      {reason === null ? null : (
                        <Typography
                          component="span"
                          variant="body2"
                          display="block"
                        >
                          {reason}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </Stack>

      {chosen === null ? null : (
        <TextField
          select
          size="small"
          label={t("access.addInvolvements.onBehalfOf")}
          disabled={disabled}
          value={offerKey}
          onChange={(event) => {
            setOfferKey(event.target.value);
          }}
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: (value) =>
                chosen.offers.find((offered) => offered.key === value)?.label ??
                t("access.addInvolvements.onBehalfOfPlaceholder"),
            },
            inputLabel: { shrink: true },
          }}
        >
          {chosen.offers.map((offered) => (
            <MenuItem key={offered.key} value={offered.key}>
              {offered.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      {offer?.distant === true ? (
        <DistanceWarning
          organisation={offer.chosen.membership?.organisationName ?? null}
        />
      ) : null}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="outlined"
          disabled={disabled || offer === undefined}
          onClick={() => {
            const membership = offer?.chosen.membership;

            if (
              chosen === null ||
              offer === undefined ||
              membership === null ||
              membership === undefined
            ) {
              return;
            }

            onList({
              party: {
                kind: "user",
                userId: chosen.user.id,
                onBehalfOfOrganisationId: membership.organisationId,
              },
              label: t("access.addInvolvements.chosen", {
                name: chosen.user.name,
                organisation: offer.label,
              }),
              organisationName: null,
            });
            reset();
          }}
        >
          {t("access.addInvolvements.addUser")}
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * Finds a body of a kind that may hold the role.
 *
 * Lists as soon as it opens, like `OrganisationPicker`: which bodies exist, and
 * of what kind, is a matter of public record.
 */
const OrganisationAdder = ({
  kind,
  search,
  unavailable,
  disabled,
  onList,
}: {
  kind: PersonInvolvementKind;
  search: PartySearch;
  unavailable: Unavailable;
  disabled: boolean;
  onList: (listed: Listed) => void;
}) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const [chosen, setChosen] = useState<Organisation | null>(null);

  const { results, loading } = useSettledResults(
    term.trim(),
    chosen === null,
    (value) =>
      search.findOrganisations(organisationTypesEligibleFor(kind), value),
  );

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <TextField
          size="small"
          label={t("access.addInvolvements.searchOrganisations")}
          disabled={disabled}
          value={term}
          onChange={(event) => {
            setChosen(null);
            setTerm(event.target.value);
          }}
        />

        {loading ? <Searching /> : null}

        {chosen === null && !loading && results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("access.picker.noOrganisations")}
          </Typography>
        ) : null}

        {chosen === null ? (
          <List dense disablePadding>
            {results.map((organisation) => {
              const reason = unavailable(
                partyKey({
                  kind: "organisation",
                  organisationId: organisation.id,
                }),
              );

              return (
                <ListItemButton
                  key={organisation.id}
                  disabled={disabled || reason !== null}
                  onClick={() => {
                    setChosen(organisation);
                    setTerm(organisation.name);
                  }}
                >
                  <ListItemText
                    primary={organisation.name}
                    secondary={
                      reason ?? t(`organisation.type.${organisation.type}`)
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        ) : null}
      </Stack>

      {chosen !== null &&
      isOrganisationDistantFromPerson({
        organisationId: chosen.id,
        near: search.near,
      }) ? (
        <DistanceWarning organisation={chosen.name} />
      ) : null}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="outlined"
          disabled={disabled || chosen === null}
          onClick={() => {
            if (chosen === null) {
              return;
            }

            onList({
              party: { kind: "organisation", organisationId: chosen.id },
              label: chosen.name,
              organisationName: chosen.name,
            });
            setChosen(null);
            setTerm("");
          }}
        >
          {t("access.addInvolvements.addOrganisation")}
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * Adds parties to one role, as one confirmation: the role is chosen first, on
 * the case involvement tab, and everybody taking it is listed here.
 *
 * Finds people by name alone and never shows an address: anybody may be found
 * in order to be involved, which is only safe while finding discloses no more
 * than a name and a body.
 */
const Adding = ({
  adding: { kind, partyKind },
  personName,
  involvements,
  search,
  onAdd,
  onRefresh,
  onClose,
}: Omit<AddInvolvementsDialogProps, "adding"> & { adding: AddingTo }) => {
  const { t } = useTranslation();
  const [listed, setListed] = useState<Listed[]>([]);
  const [status, setStatus] = useState<InvolvementChangeStatus | undefined>(
    undefined,
  );

  const name = personName;
  const holders = unendedHoldersOf(involvements, kind);
  const excludedHolders = unendedHoldersOf(
    involvements,
    involvementKindExcludedBy(kind),
  );
  const listedKeys = new Set(listed.map((entry) => partyKey(entry.party)));

  const unavailable: Unavailable = (key) => {
    if (holders.has(key)) {
      return t(`access.addInvolvements.alreadyHolds.${kind}`, { name });
    }

    if (excludedHolders.has(key)) {
      return t(`access.addInvolvements.excluded.${kind}`, { name });
    }

    return listedKeys.has(key) ? t("access.addInvolvements.listed") : null;
  };

  const full = isInvolvementKindHeldByOneAtMost(kind) && listed.length > 0;
  // What is sent is what was listed when it was confirmed, so the list and the
  // dialog stay as they are until the write answers.
  const submitting = status === "submitting";
  const title = t(`access.addInvolvements.title.${partyKind}.${kind}`);
  // The title for bodies with View names the role ("with View"), which the
  // button that adds them need not repeat.
  const confirm =
    partyKind === "organisation" && !isInvolvementKindHeldByOneAtMost(kind)
      ? t("access.addInvolvements.confirmOrganisations")
      : title;

  // Editing the list answers a failure, but not a view gone out of date: only
  // Refresh answers that.
  const clearFailure = () => {
    setStatus((current) => (current === "failed" ? undefined : current));
  };

  const list = (entry: Listed) => {
    clearFailure();
    setListed((current) => [...current, entry]);
  };

  const submit = async () => {
    const request = StartInvolvementsRequestSchema.safeParse({
      kind,
      parties: listed.map((entry) => entry.party),
    });

    // Unreachable while the dialog's checks match the schema: `AddingTo` offers
    // startable kinds, confirm waits for a party, a one-holder kind's adders stop
    // at one, and a listed party is unavailable. Kept so a drift fails politely.
    /* v8 ignore if */
    if (!request.success) {
      setStatus("failed");
      return;
    }

    setStatus("submitting");
    const outcome = await outcomeOfChange(() => onAdd(request.data));

    if (outcome === "done") {
      onClose();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" icon={false}>
            {t(`access.addInvolvements.info.${partyKind}.${kind}`, { name })}
          </Alert>

          {partyKind === "user" ? (
            <PersonAdder
              kind={kind}
              search={search}
              unavailable={unavailable}
              disabled={full || submitting}
              onList={list}
            />
          ) : (
            <OrganisationAdder
              kind={kind}
              search={search}
              unavailable={unavailable}
              disabled={full || submitting}
              onList={list}
            />
          )}

          {full ? (
            <Typography variant="body2" color="text.secondary">
              {t("access.addInvolvements.oneOnly")}
            </Typography>
          ) : null}

          {listed.length === 0 ? null : (
            <Stack spacing={1}>
              {listed.map((entry) => (
                <Paper
                  key={partyKey(entry.party)}
                  variant="outlined"
                  sx={{ px: 2, py: 1 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {entry.label}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={submitting}
                      aria-label={t("access.addInvolvements.remove", {
                        name: entry.label,
                      })}
                      onClick={() => {
                        clearFailure();
                        setListed((current) =>
                          current.filter((other) => other !== entry),
                        );
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {entry.organisationName === null ? null : (
                    <Typography variant="body2" color="text.secondary">
                      {t("access.addInvolvements.consequence", {
                        organisation: entry.organisationName,
                        name,
                      })}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {match(status)
            .with(undefined, "submitting", () => null)
            .with("failed", () => (
              <Alert severity="error">
                {t("access.addInvolvements.failed")}
              </Alert>
            ))
            .with("changed", () => (
              <ChangedSinceOpenedAlert
                message={t("access.addInvolvements.changed")}
                onRefresh={onRefresh}
                onClose={onClose}
              />
            ))
            .exhaustive()}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: onClose,
          disabled: submitting,
        }}
        proceed={{
          label: confirm,
          onClick: submit,
          disabled: listed.length === 0 || submitting || status === "changed",
          kind: "primary",
        }}
      />
    </Dialog>
  );
};

/**
 * The dialog behind a role's Add on the case involvement tab.
 *
 * What it holds is reset each time it opens, by remounting it for the role:
 * a list built for one role is never right for another.
 */
export const AddInvolvementsDialog = ({
  adding,
  ...props
}: AddInvolvementsDialogProps) =>
  adding === null ? null : (
    <Adding
      key={`${adding.partyKind}|${adding.kind}`}
      adding={adding}
      {...props}
    />
  );
